import { useEffect, useState } from "react"
import { Link } from "@remix-run/react"
import logo from "~/assets/logo.webp"
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "~/components/ui/dialog"
import { cn } from "~/utils/shadecn"
import { promptCopy, surveyOpenPath, type SurveySegment } from "~/utils/survey"

// Session cookie (no expiry): the prompt shows at most once per visit.
const SEEN_COOKIE = "mlnp_survey_prompt_seen"
const hasSeenPrompt = () => document.cookie.split("; ").some((cookie) => cookie.startsWith(`${SEEN_COOKIE}=`))
const markPromptSeen = () => {
  document.cookie = `${SEEN_COOKIE}=1; path=/; SameSite=Lax`
}

// Survey brand palette: terracotta accent on the forms' off-white, matching the Typeform theme.
const TERRACOTTA = "#BF6040"

const Wordmark = () => (
  <div
    aria-hidden
    className="h-[27px] w-[96px]"
    style={{
      backgroundColor: TERRACOTTA,
      maskImage: `url(${logo})`,
      WebkitMaskImage: `url(${logo})`,
      maskSize: "contain",
      WebkitMaskSize: "contain",
      maskRepeat: "no-repeat",
      WebkitMaskRepeat: "no-repeat",
    }}
  />
)

const CreditBadge = ({ className }: { className: string }) => (
  <div
    aria-hidden
    className={cn("flex flex-none items-center justify-center rounded-full font-bold text-[#F6F5F3]", className)}
    style={{ backgroundColor: TERRACOTTA }}>
    +1
  </div>
)

// Same tab. The survey's closing button brings them back to the homepage.
const SurveyLink = ({ href, className, onClick, children }: { href: string; className: string; onClick?: () => void; children: React.ReactNode }) => (
  <a
    href={href}
    onClick={onClick}
    className={cn(
      "flex items-center justify-center bg-[#A9532F] font-semibold text-white hover:bg-[#954828] focus:outline-none focus:ring-2 focus:ring-[#A9532F] focus:ring-offset-2",
      className
    )}>
    {children}
  </a>
)

export const SurveyPrompt = ({ segment }: { segment?: SurveySegment | null }) => {
  const [open, setOpen] = useState(false)

  // Decided after mount so the server render and hydration agree; cookies are client-only here.
  useEffect(() => {
    if (!segment || hasSeenPrompt()) return
    markPromptSeen()
    setOpen(true)
  }, [segment])

  if (!segment) return null
  const copy = promptCopy(segment)

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="flex flex-col gap-7 bg-[#F6F5F3] px-6 pb-7 pt-9 text-[#211D1C] sm:max-w-[480px] sm:px-10 sm:pb-8 sm:pt-10">
        <Wordmark />
        <div className="flex flex-col gap-3">
          <DialogTitle className="text-balance text-[26px] font-bold leading-8 tracking-[-0.02em] text-[#211D1C] sm:text-[28px] sm:leading-[34px]">
            {copy.title}
          </DialogTitle>
          <DialogDescription className="text-base leading-[25px] text-[#4A4340]">{copy.body}</DialogDescription>
        </div>
        <div className="flex items-center gap-4 rounded-lg border border-[#E6CBBE] bg-[#F2E4DD] px-4 py-[18px] sm:px-5">
          <CreditBadge className="h-10 w-10 text-[17px] sm:h-12 sm:w-12 sm:text-xl" />
          <div className="flex flex-col gap-0.5">
            <p className="text-[17px] font-bold leading-[22px]">Get 1 free credit</p>
            <p className="text-sm leading-5 text-[#5E5652]">{copy.creditDetail}</p>
          </div>
        </div>
        <div className="flex flex-col gap-2.5">
          <SurveyLink href={surveyOpenPath("popup")} onClick={() => setOpen(false)} className="h-[52px] rounded-lg text-[17px]">
            Take the survey
          </SurveyLink>
          <p className="text-center text-[13px] leading-[18px] text-[#776E69]">{copy.duration}</p>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="mx-auto h-10 text-[15px] font-medium text-[#776E69] underline underline-offset-[3px] hover:text-[#211D1C]">
            Maybe later
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** Survey entry in the notifications feed, styled to match the pop-up rather than the red account alerts. */
export const SurveyNotificationCard = ({
  post,
  onNavigate,
}: {
  post: { Title: string; Description: string; ActionLink: string; ActionText: string }
  onNavigate?: () => void
}) => (
  <div className="flex flex-col gap-3 rounded-lg border border-[#E6CBBE] bg-[#F2E4DD] p-4 text-[#211D1C]">
    <div className="flex items-start gap-3">
      <CreditBadge className="h-9 w-9 text-sm" />
      <div className="flex flex-col gap-1">
        <h4 className="text-sm font-semibold sm:text-base">{post.Title}</h4>
        <p className="text-sm text-[#5E5652]">{post.Description}</p>
      </div>
    </div>
    <SurveyLink href={post.ActionLink} onClick={onNavigate} className="h-10 rounded-md text-sm">
      {post.ActionText}
    </SurveyLink>
  </div>
)

const Confetti = () => (
  <svg aria-hidden width="132" height="84" viewBox="0 0 132 84" className="pointer-events-none absolute right-2.5 top-2.5">
    <rect x="96" y="10" width="14" height="5" rx="2.5" transform="rotate(-28 103 12.5)" fill="#F2E4DD" />
    <rect x="58" y="22" width="11" height="4" rx="2" transform="rotate(34 63.5 24)" fill="#E8A587" />
    <rect x="112" y="46" width="12" height="4.5" rx="2.25" transform="rotate(-62 118 48)" fill="#E8A587" />
    <rect x="78" y="58" width="9" height="4" rx="2" transform="rotate(18 82.5 60)" fill="#F6F5F3" opacity="0.55" />
    <circle cx="86" cy="30" r="3" fill="#F6F5F3" />
    <circle cx="122" cy="22" r="2.2" fill="#E8A587" />
    <circle cx="98" cy="72" r="2.4" fill="#F2E4DD" />
    <circle cx="44" cy="8" r="1.8" fill="#F2E4DD" opacity="0.7" />
    <path d="M68 44 l2.2 4.4 4.4 2.2 -4.4 2.2 -2.2 4.4 -2.2 -4.4 -4.4 -2.2 4.4 -2.2 z" fill="#F6F5F3" />
  </svg>
)

/** The bell's "your free credit is here" moment: solid terracotta, a big +1 and a little confetti. */
export const SurveyCreditCard = ({
  post,
  onNavigate,
}: {
  post: { Title: string; Description: string; ActionLink: string; ActionText: string }
  onNavigate?: () => void
}) => (
  <div className="relative flex flex-col gap-[18px] overflow-hidden rounded-[10px] bg-[#A9532F] px-5 pb-5 pt-[22px]">
    <Confetti />
    <div className="flex items-end gap-3">
      <span aria-hidden className="text-[56px] font-extrabold leading-[52px] tracking-[-0.04em] text-[#F6F5F3]">
        +1
      </span>
      <div className="flex flex-col gap-0.5 pb-1 text-[#F2E4DD]">
        <span className="text-xs font-bold tracking-[0.14em]">SURVEY REWARD</span>
        <span className="text-[13px] leading-[18px]">Added to your account</span>
      </div>
    </div>
    <div className="flex flex-col gap-1.5">
      <h4 className="text-lg font-semibold leading-6 tracking-[-0.01em] text-[#F6F5F3]">{post.Title}</h4>
      <p className="text-sm text-[#F2E4DD]">{post.Description}</p>
    </div>
    <Link
      to={post.ActionLink}
      onClick={onNavigate}
      className="flex h-[42px] items-center justify-center rounded-md bg-[#F6F5F3] text-sm font-semibold text-[#211D1C] hover:bg-white focus:outline-none focus:ring-2 focus:ring-[#F6F5F3] focus:ring-offset-2 focus:ring-offset-[#A9532F]">
      {post.ActionText}
    </Link>
  </div>
)
